"use client";
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps -- legacy wizard context */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "@/context/AuthContext";
import { authorizedFetch } from "@/lib/auth/authorized-fetch";
import { ingestWizardStagedFiles } from "@/lib/wizard/ingest-staged-files";
import { parseFinancialTimeSeries } from "@/lib/utils/financialUtils";
import { saveFiles, getCompanyFilesAsFileObjects, deleteCompanyFiles, saveWizardData, getWizardData, deleteWizardData } from "@/lib/utils/fileStorage";

interface DataIngestionState {
    currentStep: number;
    totalSteps: number;
    isUploading: boolean;
    isProcessing: boolean;
    formData: any;
    currency: string;
    files: File[];
    structuredFinancials: {
        revenue: Record<string, number>;
        ebitda: Record<string, number>;
        netProfit: Record<string, number>;
    };
    stagedFiles: File[];
}

interface DataIngestionContextType extends DataIngestionState {
    setStep: (step: number) => void;
    nextStep: () => void;
    prevStep: () => void;
    startUpload: (file: File, shouldNavigate?: boolean) => void;
    finishProcessing: () => void;
    updateFormData: (section: string, data: any) => void;
    analyzeStagedFiles: () => Promise<void>;
    analysisError: string | null;
    clearAnalysisError: () => void;
    removeStagedFile: (index: number) => void;
    updateStructuredFinancials: (updates: Partial<DataIngestionState['structuredFinancials']>, fromUserEdit?: boolean) => void;
    persistedFileCount: number;
    clearCompanyFiles: () => Promise<void>;
}

const DataIngestionContext = createContext<DataIngestionContextType | undefined>(undefined);

export const DataIngestionProvider = ({ children }: { children: ReactNode }) => {
    const { activeCompany, user } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState<any>({});
    const [stagedFiles, setStagedFiles] = useState<File[]>([]);
    const [structuredFinancials, setStructuredFinancials] = useState<any>({ revenue: {}, ebitda: {}, netProfit: {} });
    const [isHydrated, setIsHydrated] = useState(false);
    const [isCloudHydrated, setIsCloudHydrated] = useState(false);
    const [persistedFileCount, setPersistedFileCount] = useState(0);
    const [analysisError, setAnalysisError] = useState<string | null>(null);
    const cloudSyncTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearAnalysisError = useCallback(() => setAnalysisError(null), []);
    
    // CRITICAL: Flag to prevent text parsing from overwriting user edits
    // When user directly edits financial values, we skip the text parsing useEffect
    const skipTextParsingRef = React.useRef(false);

    const currency = activeCompany?.reportingCurrency || formData?.financials?.currency || "USD";

    // 1. Load data when activeCompany changes or on initial mount
    // Priority: Cloud (authoritative) > IndexedDB (local cache)
    useEffect(() => {
        setIsHydrated(false);
        setIsCloudHydrated(false);

        const loadData = async () => {
            // Reset to defaults before hydrating
            setCurrentStep(0);
            setFormData({});
            setStructuredFinancials({ revenue: {}, ebitda: {}, netProfit: {} });

            // Try IndexedDB for wizard data (local cache for large data)
            if (activeCompany?.id) {
                try {
                    const wizardData = await getWizardData(activeCompany.id);
                    if (wizardData && wizardData.formData && Object.keys(wizardData.formData).length > 0) {
                        console.log("📂 Loading wizard data from IndexedDB");
                        setFormData(wizardData.formData);
                        setCurrentStep(wizardData.currentStep ?? 0);
                        if (wizardData.structuredFinancials) {
                            setStructuredFinancials({
                                revenue: wizardData.structuredFinancials.revenue || {},
                                ebitda: wizardData.structuredFinancials.ebitda || {},
                                netProfit: wizardData.structuredFinancials.netProfit || {}
                            });
                        }
                    }
                } catch (err) {
                    console.warn("IndexedDB wizard data load failed:", err);
                }
            }

            // Load persisted files from IndexedDB for this company
            if (activeCompany?.id) {
                try {
                    const files = await getCompanyFilesAsFileObjects(activeCompany.id);
                    if (files.length > 0) {
                        console.log(`📁 Restored ${files.length} persisted file(s) for company ${activeCompany.id}`);
                        setStagedFiles(files);
                        setPersistedFileCount(files.length);
                    } else {
                        setStagedFiles([]);
                        setPersistedFileCount(0);
                    }
                } catch (err) {
                    console.error("Failed to load persisted files:", err);
                    setStagedFiles([]);
                    setPersistedFileCount(0);
                }
            } else {
                setStagedFiles([]);
                setPersistedFileCount(0);
            }

            // Cloud hydrate (cross-device sync)
            if (user?.id && activeCompany?.id) {
                try {
                    const res = await fetch(
                        `/api/cloud/company-blobs?companyId=${encodeURIComponent(activeCompany.id)}&blobType=data_ingestion`
                    );
                    if (res.ok) {
                        const cloud = await res.json();
                        if (cloud?.success && cloud?.payload) {
                            const payload = cloud.payload;
                            if (typeof payload.currentStep === "number") setCurrentStep(payload.currentStep);
                            if (payload.formData && typeof payload.formData === "object") setFormData(payload.formData);
                            if (payload.structuredFinancials) {
                                setStructuredFinancials({
                                    revenue: payload.structuredFinancials.revenue || {},
                                    ebitda: payload.structuredFinancials.ebitda || {},
                                    netProfit: payload.structuredFinancials.netProfit || {},
                                });
                            }
                            console.log("☁️ Hydrated Data Ingestion state from cloud");
                        }
                    }
                } catch (err) {
                    console.warn("Cloud hydrate failed for data ingestion:", err);
                }
            }

            setIsHydrated(true);
            setIsCloudHydrated(true);
        };

        loadData();
    }, [activeCompany?.id, user?.id]);

    // 2. Persist to IndexedDB (local cache for large data)
    useEffect(() => {
        if (!isHydrated || !isCloudHydrated || !activeCompany?.id) return;
        saveWizardData(activeCompany.id, formData, structuredFinancials, currentStep)
            .catch(err => console.error("Failed to save wizard data to IndexedDB:", err));
    }, [currentStep, formData, isHydrated, isCloudHydrated, activeCompany, structuredFinancials]);

    // Cloud sync: persist key ingestion state per company
    useEffect(() => {
        if (!isHydrated || !isCloudHydrated || !activeCompany?.id || !user?.id) return;

        if (cloudSyncTimeoutRef.current) clearTimeout(cloudSyncTimeoutRef.current);
        cloudSyncTimeoutRef.current = setTimeout(async () => {
            try {
                await fetch("/api/cloud/company-blobs", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        companyId: activeCompany.id,
                        blobType: "data_ingestion",
                        payload: {
                            currentStep,
                            formData,
                            structuredFinancials,
                        },
                    }),
                });
            } catch (err) {
                console.warn("Cloud sync failed for data ingestion:", err);
            }
        }, 500);

        return () => {
            if (cloudSyncTimeoutRef.current) clearTimeout(cloudSyncTimeoutRef.current);
        };
    }, [user?.id, activeCompany?.id, currentStep, formData, structuredFinancials, isHydrated, isCloudHydrated]);

    // 3. Parse Financials into Structured Schema whenever formData.financials changes
    // CRITICAL: This should MERGE parsed values with existing values, not replace them
    // Direct updates via chat (through updateStructuredFinancials) should NOT be overwritten
    // 
    // Strategy: Only add parsed values if the year doesn't already exist in structuredFinancials
    // This preserves direct numeric updates while still allowing text parsing for new data
    const lastParsedFinancialsRef = React.useRef<string>("");
    
    useEffect(() => {
        if (!formData?.financials) return;
        
        // CRITICAL: Skip if this update came from user's direct edit
        // The FinancialDataStep already updated structuredFinancials directly
        if (skipTextParsingRef.current) {
            console.log("📊 Skipping text parsing - user edit in progress");
            skipTextParsingRef.current = false;  // Reset flag
            return;
        }
        
        // Create a hash of the current financials text to detect actual changes
        const financialsHash = JSON.stringify({
            revenue: formData.financials.revenue,
            ebitda: formData.financials.ebitda,
            netProfit: formData.financials.netProfit
        });
        
        // Skip if the text hasn't actually changed (prevents unnecessary overwrites)
        if (financialsHash === lastParsedFinancialsRef.current) {
            return;
        }
        lastParsedFinancialsRef.current = financialsHash;
        
        const rev = parseFinancialTimeSeries(formData.financials.revenue);
        const ebitda = parseFinancialTimeSeries(formData.financials.ebitda);
        const net = parseFinancialTimeSeries(formData.financials.netProfit);
        
        // CRITICAL: MERGE parsed values with existing values
        // NEW parsed values override old ones (to fix truncated/stale data)
        // but only when the new value is a valid non-zero number
        const smartMerge = (existing: Record<string, number>, parsed: Record<string, number>) => {
            const result = { ...existing };
            for (const key of Object.keys(parsed)) {
                if (typeof parsed[key] === 'number' && parsed[key] !== 0) {
                    result[key] = parsed[key]; // New non-zero value overwrites old
                }
            }
            return result;
        };
        
        setStructuredFinancials((prev: any) => {
            const merged = {
                revenue: smartMerge(prev.revenue, rev),
                ebitda: smartMerge(prev.ebitda, ebitda),
                netProfit: smartMerge(prev.netProfit, net)
            };
            console.log("📊 Merged structuredFinancials:", { parsed: { rev, ebitda, net }, prev, merged });
            return merged;
        });
    }, [formData?.financials?.revenue, formData?.financials?.ebitda, formData?.financials?.netProfit]);

    const totalSteps = 6;

    const setStep = (step: number) => {
        if (step >= 0 && step <= totalSteps) setCurrentStep(step);
    };

    const nextStep = () => {
        if (currentStep < totalSteps) setCurrentStep(prev => prev + 1);
    };

    const prevStep = () => {
        if (currentStep > 0) setCurrentStep(prev => prev - 1);
    };

    // Staging Logic: Store in memory AND persist to IndexedDB
    const startUpload = async (file: File, shouldNavigate: boolean = true) => {
        setIsUploading(true);
        try {
            // Add to in-memory state
            setStagedFiles(prev => [...prev, file]);
            
            // Persist to IndexedDB so files survive page refreshes
            if (activeCompany?.id) {
                await saveFiles(activeCompany.id, [file], 'document');
                setPersistedFileCount(prev => prev + 1);
                console.log(`📁 File "${file.name}" persisted for company ${activeCompany.id}`);
            }
        } catch (err) {
            console.error("Failed to persist file:", err);
        }
        setIsUploading(false);
    };

    const removeStagedFile = (index: number) => {
        setStagedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Trigger Analysis on ALL staged files
    const analyzeStagedFiles = async () => {
        if (stagedFiles.length === 0) {
            setAnalysisError("No files are ready for analysis. Please upload a document and wait until it shows Ready.");
            return;
        }

        setAnalysisError(null);
        setIsProcessing(true);
        try {
            // Persist files to IndexedDB before analysis (ensures they survive even if analysis fails)
            if (activeCompany?.id) {
                try {
                    await saveFiles(activeCompany.id, stagedFiles, 'document');
                    setPersistedFileCount(stagedFiles.length);
                    console.log(`📁 Persisted ${stagedFiles.length} file(s) to IndexedDB for company ${activeCompany.id}`);
                } catch (err) {
                    console.error("Failed to persist files to IndexedDB:", err);
                    // Continue with analysis even if persistence fails
                }
            }

            const result = await ingestWizardStagedFiles(
                stagedFiles,
                activeCompany?.id,
            );

            if (result.data) {
                setFormData(result.data);
            }

            // Navigate to Step 1 only after successful analysis
            setCurrentStep(1);

        } catch (error) {
            console.error("Analysis error:", error);
            const message =
                error instanceof Error
                    ? error.message
                    : "Analysis failed. Please try again or upload a PDF, Word, or PowerPoint file.";
            if (message.includes("401") || message.toLowerCase().includes("unauthorized")) {
                setAnalysisError("Your session expired. Please sign in again and retry.");
            } else if (message.includes("Failed to parse body as FormData")) {
                setAnalysisError(
                    "Upload failed — the file could not be sent to the server. Try refreshing the page or uploading a smaller file.",
                );
            } else if (message.includes("too large") || message.includes("50 MB")) {
                setAnalysisError(message);
            } else {
                setAnalysisError(message);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const finishProcessing = () => {
        setIsProcessing(false);
    };

    const updateFormData = (section: string, data: any) => {
        setFormData((prev: any) => ({ ...prev, [section]: data }));
    };

    const updateStructuredFinancials = (updates: Partial<DataIngestionState['structuredFinancials']>, fromUserEdit: boolean = false) => {
        // If this is from a user edit, skip the text parsing useEffect
        // This prevents the parsed values from overwriting user's direct edits
        if (fromUserEdit) {
            skipTextParsingRef.current = true;
        }
        
        setStructuredFinancials((prev: any) => {
            const updated = {
                revenue: { ...prev.revenue, ...(updates.revenue || {}) },
                ebitda: { ...prev.ebitda, ...(updates.ebitda || {}) },
                netProfit: { ...prev.netProfit, ...(updates.netProfit || {}) }
            };
            console.log("💾 updateStructuredFinancials called:", { updates, fromUserEdit, prev, updated });
            return updated;
        });
    };

    // Clear all persisted files for the current company
    const clearCompanyFilesHandler = useCallback(async () => {
        if (!activeCompany?.id) return;
        try {
            const count = await deleteCompanyFiles(activeCompany.id);
            setStagedFiles([]);
            setPersistedFileCount(0);
            console.log(`🗑️ Cleared ${count} persisted files for company ${activeCompany.id}`);
        } catch (err) {
            console.error("Failed to clear company files:", err);
        }
    }, [activeCompany?.id]);

    return (
        <DataIngestionContext.Provider
            value={{
                currentStep,
                totalSteps,
                isUploading,
                isProcessing,
                formData,
                currency,
                files: [], // Deprecated in favor of stagedFiles but kept for type safety if needed
                structuredFinancials,
                stagedFiles,
                setStep,
                nextStep,
                prevStep,
                startUpload,
                finishProcessing,
                updateFormData,
                analyzeStagedFiles,
                analysisError,
                clearAnalysisError,
                removeStagedFile,
                updateStructuredFinancials,
                persistedFileCount,
                clearCompanyFiles: clearCompanyFilesHandler
            }}
        >
            {children}
        </DataIngestionContext.Provider>
    );
};

export const useDataIngestion = () => {
    const context = useContext(DataIngestionContext);
    if (context === undefined) {
        throw new Error("useDataIngestion must be used within a DataIngestionProvider");
    }
    return context;
};
